import { Stack, useTheme } from '@mui/system';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { TextField, Tooltip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { memo, useCallback, useEffect, useState } from 'react';
import {
  QuoteStatus,
  UserPermission,
  UserRoleCategoryPath,
} from '../../../../enums';
import {
  useAuthentication,
  useCompanyCustomParts,
  useCurrentUser,
  usePermission,
  useQuotableCables,
} from '../../../../hooks';
import { PricedQuoteStatuses } from '../../../../mappings';
import { UnderlinedLink } from '../../../UnderlinedLink/UnderlinedLink.component';
import { CUSTOM_PART_NUMBER } from '../../constants';
import * as Sx from '../QuoteProductQuantity/QuoteProductQuantity.styles';
import { DEFAULT_PLACEHOLDER } from '../../../InventoryFilters/InventoryFilters.constants';
import { Cable } from '../../../../models';
import { QuoteProductPartNumberProps } from './QuoteProductPartNumber.props';

export const QuoteProductPartNumber = memo(
  ({
    company,
    row,
    isValid,
    status,
    width = '130px',
    updateProduct,
    products,
    isReadonly,
  }: QuoteProductPartNumberProps) => {
    const { t } = useTranslation();
    const { userRoleCategory } = useAuthentication();
    const currentUser = useCurrentUser();
    const { palette } = useTheme();
    const { hasOneOfPermissions, hasPermission } = usePermission();
    const [value, setValue] = useState(row.partNumber);
    const [searchedPartNumber, setSearchedPartNumber] = useState(
      row.partNumber
    );
    const [error, setError] = useState<boolean>(!value);
    const [companyId, setCompanyId] = useState<string>(
      company?.id || currentUser.companyRef.id
    );

    useEffect(() => {
      if (company) {
        setCompanyId(company.id);
      } else {
        setCompanyId(currentUser.companyRef.id);
      }
    }, [company, currentUser.companyRef.id]);

    const { list: cables } = useQuotableCables();
    const { list: parts } = useCompanyCustomParts(companyId);

    const findCable = useCallback(
      (id: string, search = true, cablesList = cables, customParts = parts) => {
        if (
          search &&
          company &&
          company.hasCustomPartNumbers &&
          parts.length > 0
        ) {
          const customPart = customParts.find((part) => part.partNumber === id);
          return customPart
            ? cablesList.find((cable) => cable.id === customPart.id)
            : null;
        }

        return cablesList.find((cable) => cable.id === id);
      },
      [cables, company, parts]
    );

    const handleSearch = (id: string) => {
      if (!updateProduct || !products || id === searchedPartNumber) return;
      setSearchedPartNumber(id);
      const cable = findCable(id);

      if (cable) {
        setError(false);
        updateProduct?.(row.lineItem, cable);
      } else {
        setError(true);
        updateProduct?.(row.lineItem, {
          id: '',
          description: '',
        } as Cable);
      }
    };

    const inventoryLink = `${UserRoleCategoryPath[userRoleCategory]}/inventory?part=${row.normalizedId}`;

    const [showLink, setShowLink] = useState(false);

    useEffect(() => {
      setShowLink(
        Boolean(
          isReadonly &&
            status &&
            [QuoteStatus.Cancelled, ...PricedQuoteStatuses].includes(status) &&
            hasPermission(UserPermission.ViewInventory) &&
            hasOneOfPermissions([
              UserPermission.ManageQuotes,
              UserPermission.RequestQuotes,
              UserPermission.ViewAgencyQuotes,
              UserPermission.ViewCompanyQuotes,
            ]) &&
            ((row.isCSA && currentUser.canSeeCSA) ||
              (row.isUL && currentUser.canSeeUL)) &&
            row.partNumber !== CUSTOM_PART_NUMBER
        )
      );
    }, [
      isReadonly,
      status,
      hasPermission,
      hasOneOfPermissions,
      row,
      currentUser,
    ]);

    useEffect(() => {
      if (value) {
        const cable = findCable(value, false);
        setError(!cable);
      }
    }, [findCable, value]);

    return (
      <Stack spacing={1} direction="row" alignItems="center">
        {showLink && value ? (
          <UnderlinedLink openInNewTab to={inventoryLink}>
            {value}
          </UnderlinedLink>
        ) : value === CUSTOM_PART_NUMBER || isReadonly ? (
          <Typography>{value}</Typography>
        ) : (
          <TextField
            value={value}
            onBlur={(e) => handleSearch(e.target.value)}
            onChange={(e) => {
              if (!e.target.value) setError(true);
              setValue(e.target.value);
            }}
            error={error}
            sx={{ width, ...Sx.textFieldNumericStyles }}
            inputProps={{
              minLength: 6,
              placeholder: DEFAULT_PLACEHOLDER,
            }}
          />
        )}
        {!isValid(row) && (
          <Tooltip
            title={t(
              'quotes.edit.products.table.validation.invalidMarketSingle'
            )}
          >
            <ErrorOutlineIcon htmlColor={palette.error.main} fontSize="small" />
          </Tooltip>
        )}
      </Stack>
    );
  }
);
